import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Copy, Check, Link2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLORS = {
  organic: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid: 'bg-blue-50 text-blue-700 border-blue-200',
  email: 'bg-purple-50 text-purple-700 border-purple-200',
  referral: 'bg-amber-50 text-amber-700 border-amber-200',
  offline: 'bg-slate-50 text-slate-700 border-slate-200',
};

function buildUtmUrl(baseUrl, source, medium, campaign, content) {
  if (!baseUrl) return '';
  const params = new URLSearchParams();
  if (source) params.set('utm_source', source);
  if (medium) params.set('utm_medium', medium);
  if (campaign) params.set('utm_campaign', campaign);
  if (content) params.set('utm_content', content);
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}${params.toString()}`;
}

export default function ClientUTMTab({ clientId, clientName, websiteUrl }) {
  const [utms, setUtms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [baseUrl, setBaseUrl] = useState(websiteUrl || '');

  const fetchUtms = async () => {
    try {
      const data = await api(`/api/clients/${clientId}/utms`);
      setUtms(data);
    } catch (err) {
      toast.error('Failed to load UTMs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUtms(); }, [clientId]);

  const toggleActive = (defaultId) => {
    setUtms(prev => prev.map(u =>
      u.default_id === defaultId ? { ...u, active: !u.active } : u
    ));
  };

  const updateField = (defaultId, field, value) => {
    setUtms(prev => prev.map(u =>
      u.default_id === defaultId ? { ...u, [field]: value } : u
    ));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}/utms-bulk`, {
        method: 'PUT',
        body: JSON.stringify({ utms: utms.map(u => ({
          default_id: u.default_id,
          active: u.active,
          campaign: u.campaign || '',
          content: u.content || '',
          custom_source: u.custom_source || '',
          custom_medium: u.custom_medium || '',
          notes: u.notes || '',
        }))})
      });
      toast.success('UTMs saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = (utm) => {
    const src = utm.custom_source || utm.source;
    const med = utm.custom_medium || utm.medium;
    const url = buildUtmUrl(baseUrl, src, med, utm.campaign, utm.content);
    if (url) {
      navigator.clipboard.writeText(url);
      setCopiedId(utm.default_id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success('UTM URL copied!');
    } else {
      toast.error('Set a base URL first');
    }
  };

  const activeUtms = utms.filter(u => u.active);
  const inactiveUtms = utms.filter(u => !u.active);

  return (
    <div className="space-y-6">
      {/* Base URL */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Client Website URL</label>
              <Input
                value={baseUrl}
                onChange={e => setBaseUrl(e.target.value)}
                placeholder="https://www.clientwebsite.com"
                className="text-sm"
              />
            </div>
            <Button onClick={handleSaveAll} disabled={saving} style={{ background: '#1f2a4d' }} className="mt-5">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save UTMs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active UTMs */}
      <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span className="w-2 h-2 rounded-full bg-emerald-500" /> Active UTMs ({activeUtms.length})
        </h3>
        {activeUtms.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No active UTMs — toggle platforms below to activate
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {activeUtms.map((utm) => {
              const src = utm.custom_source || utm.source;
              const med = utm.custom_medium || utm.medium;
              const fullUrl = buildUtmUrl(baseUrl, src, med, utm.campaign, utm.content);
              return (
                <Card key={utm.default_id} className="rounded-xl overflow-hidden">
                  <CardContent className="p-0">
                    {/* Header row */}
                    <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/20">
                      <button
                        onClick={() => toggleActive(utm.default_id)}
                        className="w-5 h-5 rounded border-2 border-emerald-500 bg-emerald-500 flex items-center justify-center flex-shrink-0"
                      >
                        <Check size={12} className="text-white" />
                      </button>
                      <span className="font-semibold text-sm flex-shrink-0">{utm.platform}</span>
                      <Badge variant="outline" className={`text-[9px] capitalize ${CATEGORY_COLORS[utm.category] || ''}`}>{utm.category}</Badge>
                      <div className="flex-1" />
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => copyUrl(utm)} disabled={!baseUrl}>
                        {copiedId === utm.default_id ? <Check size={12} className="mr-1 text-emerald-600" /> : <Copy size={12} className="mr-1" />}
                        {copiedId === utm.default_id ? 'Copied!' : 'Copy Full URL'}
                      </Button>
                    </div>
                    {/* Parameters row */}
                    <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                      <div className="space-y-1">
                        <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Source</label>
                        <Input
                          value={utm.custom_source || utm.source}
                          onChange={e => updateField(utm.default_id, 'custom_source', e.target.value)}
                          className="h-7 text-xs font-mono w-32 bg-muted/30"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Medium</label>
                        <Input
                          value={utm.custom_medium || utm.medium}
                          onChange={e => updateField(utm.default_id, 'custom_medium', e.target.value)}
                          className="h-7 text-xs font-mono w-24 bg-muted/30"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Campaign</label>
                        <Input
                          value={utm.campaign || ''}
                          onChange={e => updateField(utm.default_id, 'campaign', e.target.value)}
                          placeholder="campaign_name"
                          className="h-7 text-xs w-40"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] text-muted-foreground uppercase tracking-wider">Content</label>
                        <Input
                          value={utm.content || ''}
                          onChange={e => updateField(utm.default_id, 'content', e.target.value)}
                          placeholder="optional"
                          className="h-7 text-xs w-28"
                        />
                      </div>
                    </div>
                    {/* Full URL display */}
                    {baseUrl && (
                      <div className="px-4 py-2.5 bg-[#1f2a4d]/[0.03] border-t">
                        <div className="flex items-center gap-2">
                          <Link2 size={12} className="text-muted-foreground flex-shrink-0" />
                          <code
                            className="text-[11px] font-mono text-[#1f2a4d] bg-white border rounded px-2 py-1 flex-1 cursor-pointer hover:bg-muted/30 transition-colors truncate"
                            onClick={() => copyUrl(utm)}
                            title="Click to copy"
                          >
                            {fullUrl}
                          </code>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Inactive UTMs */}
      {inactiveUtms.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-muted-foreground">
            Available Platforms ({inactiveUtms.length})
          </h3>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Source / Medium</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inactiveUtms.map((utm) => (
                    <TableRow key={utm.default_id} className="opacity-60 hover:opacity-100 transition-opacity">
                      <TableCell>
                        <button
                          onClick={() => toggleActive(utm.default_id)}
                          className="w-5 h-5 rounded border-2 border-gray-300 hover:border-emerald-400 transition-colors"
                        />
                      </TableCell>
                      <TableCell className="font-medium text-sm">{utm.platform}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{utm.source}</code>
                          <code className="text-[10px] bg-muted px-1 py-0.5 rounded">{utm.medium}</code>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[9px] capitalize ${CATEGORY_COLORS[utm.category] || ''}`}>{utm.category}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{utm.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
